import { useSeoMeta } from '@unhead/react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { TallyDialog } from '@/components/TallyDialog';
import { 
  ClipboardList, 
  FileText, 
  Send, 
  Camera,
  Mic,
  Image as ImageIcon,
  CheckCircle2,
  Clock,
  DollarSign,
  Menu,
  Home,
  PlusCircle,
  FileCheck,
  Settings,
  ArrowLeft,
  Search,
  Filter,
  MoreVertical,
  MapPin,
  Calendar,
  User,
  Building2,
  Phone,
  Mail,
  Download,
  Share2,
  Edit,
  Trash2,
  Eye,
  ChevronRight,
  Bell,
  Zap
} from 'lucide-react';
import { useState } from 'react';

type NavView = 'home' | 'new-jobwalk' | 'proposals' | 'settings';
type ProposalStatus = 'draft' | 'sent' | 'won' | 'lost';

interface JobWalk {
  id: string;
  projectName: string;
  client: string;
  location: string;
  date: string;
  status: ProposalStatus;
  value?: string;
  notes?: string;
  images?: number;
}

const MobileApp = () => {
  useSeoMeta({
    title: 'JobWalk AI - Mobile App',
    description: 'Capture job walks, generate proposals, and manage follow-ups on the go.',
  });

  const [currentView, setCurrentView] = useState<NavView>('home');
  const [selectedJobWalk, setSelectedJobWalk] = useState<JobWalk | null>(null);
  const [tallyOpen, setTallyOpen] = useState(false);

  // Sample data
  const jobWalks: JobWalk[] = [
    {
      id: '1',
      projectName: 'Downtown Office HVAC Retrofit',
      client: 'Apex Construction',
      location: '1250 Commerce St, Dallas, TX',
      date: '2026-02-24',
      status: 'sent',
      value: '$127,500',
      notes: 'Replace existing RTU units, upgrade controls to DDC',
      images: 12
    },
    {
      id: '2',
      projectName: 'Manufacturing Facility Electrical',
      client: 'BuildCo Partners',
      location: '4800 Industrial Blvd, Fort Worth, TX',
      date: '2026-02-23',
      status: 'draft',
      value: '$89,200',
      notes: 'New 480V service, machine disconnects, lighting',
      images: 8
    },
    {
      id: '3',
      projectName: 'School Plumbing Renovation',
      client: 'Metro School District',
      location: '789 Education Way, Arlington, TX',
      date: '2026-02-22',
      status: 'won',
      value: '$156,000',
      notes: 'Replace all restroom fixtures, new water heaters',
      images: 15
    },
    {
      id: '4',
      projectName: 'Retail Strip Center Electrical',
      client: 'Redstone Developers',
      location: '2100 Main St, Plano, TX',
      date: '2026-02-20',
      status: 'sent',
      value: '$43,800',
      notes: 'Tenant improvement, new panels and circuits',
      images: 6
    }
  ];

  const getStatusColor = (status: ProposalStatus) => {
    switch (status) {
      case 'draft': return 'bg-muted text-muted-foreground';
      case 'sent': return 'bg-primary/10 text-primary';
      case 'won': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      case 'lost': return 'bg-destructive/10 text-destructive';
    }
  };

  const getStatusLabel = (status: ProposalStatus) => {
    switch (status) {
      case 'draft': return 'Draft';
      case 'sent': return 'Sent';
      case 'won': return 'Won';
      case 'lost': return 'Lost';
    }
  };

  // Home View
  const HomeView = () => (
    <div className="flex flex-col h-full">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <Card className="border-2">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <FileText className="h-5 w-5 text-primary" />
              <Badge variant="secondary" className="text-xs">This Week</Badge>
            </div>
            <div className="text-2xl font-bold">12</div>
            <p className="text-xs text-muted-foreground">Job Walks</p>
          </CardContent>
        </Card>

        <Card className="border-2">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Send className="h-5 w-5 text-accent" />
              <Badge variant="secondary" className="text-xs">Active</Badge>
            </div>
            <div className="text-2xl font-bold">8</div>
            <p className="text-xs text-muted-foreground">Proposals Out</p>
          </CardContent>
        </Card>

        <Card className="border-2">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
              <Badge variant="secondary" className="text-xs">MTD</Badge>
            </div>
            <div className="text-2xl font-bold">67%</div>
            <p className="text-xs text-muted-foreground">Win Rate</p>
          </CardContent>
        </Card>

        <Card className="border-2">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="h-5 w-5 text-primary" />
              <Badge variant="secondary" className="text-xs">Pipeline</Badge>
            </div>
            <div className="text-2xl font-bold">$1.2M</div>
            <p className="text-xs text-muted-foreground">Active Value</p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold mb-3 text-muted-foreground">Quick Actions</h3>
        <div className="grid grid-cols-3 gap-2">
          <Button 
            variant="outline" 
            className="h-auto py-4 flex flex-col gap-2"
            onClick={() => setCurrentView('new-jobwalk')}
          >
            <Camera className="h-5 w-5" />
            <span className="text-xs">New Walk</span>
          </Button>
          <Button variant="outline" className="h-auto py-4 flex flex-col gap-2">
            <FileCheck className="h-5 w-5" />
            <span className="text-xs">Review</span>
          </Button>
          <Button variant="outline" className="h-auto py-4 flex flex-col gap-2">
            <Send className="h-5 w-5" />
            <span className="text-xs">Follow-Up</span>
          </Button>
        </div>
      </div>

      {/* Recent Job Walks */}
      <div className="flex-1">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-muted-foreground">Recent Job Walks</h3>
          <Button variant="ghost" size="sm" onClick={() => setCurrentView('proposals')}>
            View All
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>

        <div className="space-y-3">
          {jobWalks.slice(0, 3).map((job) => (
            <Card 
              key={job.id} 
              className="border-2 hover:border-primary/40 transition-all cursor-pointer"
              onClick={() => {
                setSelectedJobWalk(job);
                setCurrentView('proposals');
              }}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h4 className="font-semibold text-sm mb-1">{job.projectName}</h4>
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Building2 className="h-3 w-3" />
                      {job.client}
                    </p>
                  </div>
                  <Badge className={`${getStatusColor(job.status)} text-xs`}>
                    {getStatusLabel(job.status)}
                  </Badge>
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {new Date(job.date).toLocaleDateString()}
                  </span>
                  {job.value && (
                    <span className="font-semibold text-foreground">{job.value}</span>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );

  // New Job Walk View
  const NewJobWalkView = () => {
    const [activeTab, setActiveTab] = useState('details');

    return (
      <div className="flex flex-col h-full">
        <div className="mb-4">
          <Button 
            variant="ghost" 
            size="sm" 
            className="mb-3"
            onClick={() => setCurrentView('home')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h2 className="text-2xl font-bold">New Job Walk</h2>
          <p className="text-sm text-muted-foreground">Capture details and notes</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
          <TabsList className="grid w-full grid-cols-3 mb-4">
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="notes">Notes</TabsTrigger>
            <TabsTrigger value="media">Media</TabsTrigger>
          </TabsList>

          <ScrollArea className="flex-1">
            <TabsContent value="details" className="mt-0 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="project-name">Project Name *</Label>
                <Input id="project-name" placeholder="e.g., Downtown Office HVAC" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="client">Client / GC *</Label>
                <Input id="client" placeholder="e.g., Apex Construction" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact-name">Contact Name</Label>
                  <Input id="contact-name" placeholder="John Smith" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact-phone">Phone</Label>
                  <Input id="contact-phone" type="tel" placeholder="(555) 123-4567" />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" placeholder="contact@company.com" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="location">Location *</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input id="location" placeholder="Street address" className="pl-10" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="date">Walk Date *</Label>
                  <Input id="date" type="date" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="time">Time</Label>
                  <Input id="time" type="time" />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="estimated-value">Estimated Value</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input id="estimated-value" type="number" placeholder="0" className="pl-10" />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="notes" className="mt-0 space-y-4">
              <Card className="border-2 bg-muted/30">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Zap className="h-5 w-5 text-accent mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-sm mb-1">AI-Powered Notes</h4>
                      <p className="text-xs text-muted-foreground">
                        Use voice or text. AI will structure your notes automatically.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="space-y-2">
                <Label htmlFor="scope">Scope of Work</Label>
                <Textarea 
                  id="scope" 
                  placeholder="Describe the work to be performed..."
                  rows={4}
                />
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="flex-1">
                    <Mic className="h-4 w-4 mr-2" />
                    Voice Input
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="conditions">Existing Conditions</Label>
                <Textarea 
                  id="conditions" 
                  placeholder="Note any existing conditions, access issues, etc..."
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="assumptions">Assumptions</Label>
                <Textarea 
                  id="assumptions" 
                  placeholder="What assumptions are you making?"
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="exclusions">Exclusions</Label>
                <Textarea 
                  id="exclusions" 
                  placeholder="What's NOT included in this scope?"
                  rows={3}
                />
              </div>
            </TabsContent>

            <TabsContent value="media" className="mt-0 space-y-4">
              <Card className="border-2 border-dashed hover:border-primary/40 transition-all cursor-pointer">
                <CardContent className="p-8 text-center">
                  <Camera className="h-12 w-12 mx-auto mb-3 text-muted-foreground" />
                  <h4 className="font-semibold mb-1">Take Photos</h4>
                  <p className="text-sm text-muted-foreground mb-4">
                    Capture site conditions, equipment, access points
                  </p>
                  <Button>
                    <Camera className="h-4 w-4 mr-2" />
                    Open Camera
                  </Button>
                </CardContent>
              </Card>

              <Card className="border-2 border-dashed hover:border-primary/40 transition-all cursor-pointer">
                <CardContent className="p-8 text-center">
                  <ImageIcon className="h-12 w-12 mx-auto mb-3 text-muted-foreground" />
                  <h4 className="font-semibold mb-1">Upload from Gallery</h4>
                  <p className="text-sm text-muted-foreground mb-4">
                    Select existing photos or documents
                  </p>
                  <Button variant="outline">
                    <ImageIcon className="h-4 w-4 mr-2" />
                    Choose Files
                  </Button>
                </CardContent>
              </Card>

              <div className="space-y-2">
                <h4 className="font-semibold text-sm">Uploaded Media (0)</h4>
                <Card className="border-dashed">
                  <CardContent className="p-6 text-center text-sm text-muted-foreground">
                    No media uploaded yet
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </ScrollArea>
        </Tabs>

        <div className="mt-6 pt-4 border-t space-y-2">
          <Button className="w-full" size="lg">
            <FileCheck className="h-4 w-4 mr-2" />
            Generate Proposal
          </Button>
          <Button variant="outline" className="w-full">
            Save as Draft
          </Button>
        </div>
      </div>
    );
  };

  // Proposals View
  const ProposalsView = () => {
    const [filter, setFilter] = useState<'all' | ProposalStatus>('all');
    
    const filteredJobs = filter === 'all' 
      ? jobWalks 
      : jobWalks.filter(j => j.status === filter);

    return (
      <div className="flex flex-col h-full">
        <div className="mb-4">
          <h2 className="text-2xl font-bold mb-1">Proposals</h2>
          <p className="text-sm text-muted-foreground">Manage your job walks and proposals</p>
        </div>

        {/* Search and Filter */}
        <div className="flex gap-2 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search projects..." className="pl-10" />
          </div>
          <Button variant="outline" size="icon">
            <Filter className="h-4 w-4" />
          </Button>
        </div>

        {/* Status Filter */}
        <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
          <Button 
            variant={filter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('all')}
          >
            All ({jobWalks.length})
          </Button>
          <Button 
            variant={filter === 'draft' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('draft')}
          >
            Draft ({jobWalks.filter(j => j.status === 'draft').length})
          </Button>
          <Button 
            variant={filter === 'sent' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('sent')}
          >
            Sent ({jobWalks.filter(j => j.status === 'sent').length})
          </Button>
          <Button 
            variant={filter === 'won' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('won')}
          >
            Won ({jobWalks.filter(j => j.status === 'won').length})
          </Button>
        </div>

        {/* Job List */}
        <ScrollArea className="flex-1">
          <div className="space-y-3 pb-4">
            {filteredJobs.map((job) => (
              <Card 
                key={job.id}
                className="border-2 hover:border-primary/40 transition-all"
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-start gap-2 mb-1">
                        <h4 className="font-semibold text-sm flex-1">{job.projectName}</h4>
                        <Button variant="ghost" size="icon" className="h-6 w-6 -mt-1">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
                        <Building2 className="h-3 w-3" />
                        {job.client}
                      </p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {job.location}
                      </p>
                    </div>
                  </div>

                  {job.notes && (
                    <p className="text-xs text-muted-foreground mb-3 line-clamp-2">
                      {job.notes}
                    </p>
                  )}

                  <div className="flex items-center justify-between mb-3">
                    <Badge className={`${getStatusColor(job.status)} text-xs`}>
                      {getStatusLabel(job.status)}
                    </Badge>
                    {job.value && (
                      <span className="text-sm font-semibold">{job.value}</span>
                    )}
                  </div>

                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {new Date(job.date).toLocaleDateString()}
                    </span>
                    {job.images && (
                      <span className="flex items-center gap-1">
                        <ImageIcon className="h-3 w-3" />
                        {job.images} photos
                      </span>
                    )}
                  </div>

                  <Separator className="my-3" />

                  <div className="grid grid-cols-3 gap-2">
                    <Button variant="outline" size="sm" className="text-xs">
                      <Eye className="h-3 w-3 mr-1" />
                      View
                    </Button>
                    <Button variant="outline" size="sm" className="text-xs">
                      <Edit className="h-3 w-3 mr-1" />
                      Edit
                    </Button>
                    <Button variant="outline" size="sm" className="text-xs">
                      <Share2 className="h-3 w-3 mr-1" />
                      Share
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </ScrollArea>
      </div>
    );
  };

  // Settings View
  const SettingsView = () => (
    <div className="flex flex-col h-full">
      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-1">Settings</h2>
        <p className="text-sm text-muted-foreground">Manage your account and preferences</p>
      </div>

      <ScrollArea className="flex-1">
        <div className="space-y-6">
          {/* Account Section */}
          <div>
            <h3 className="text-sm font-semibold mb-3 text-muted-foreground">Account</h3>
            <Card>
              <CardContent className="p-0">
                <button className="w-full p-4 text-left hover:bg-muted/50 transition-colors flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <User className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium text-sm">Profile Information</p>
                      <p className="text-xs text-muted-foreground">Update your details</p>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                </button>
                <Separator />
                <button className="w-full p-4 text-left hover:bg-muted/50 transition-colors flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Building2 className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium text-sm">Company Details</p>
                      <p className="text-xs text-muted-foreground">Logo, license, insurance</p>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                </button>
              </CardContent>
            </Card>
          </div>

          {/* Proposal Settings */}
          <div>
            <h3 className="text-sm font-semibold mb-3 text-muted-foreground">Proposals</h3>
            <Card>
              <CardContent className="p-0">
                <button className="w-full p-4 text-left hover:bg-muted/50 transition-colors flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium text-sm">Templates & Branding</p>
                      <p className="text-xs text-muted-foreground">Customize proposal look</p>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                </button>
                <Separator />
                <button className="w-full p-4 text-left hover:bg-muted/50 transition-colors flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <ClipboardList className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium text-sm">Default Terms</p>
                      <p className="text-xs text-muted-foreground">Payment, warranty, exclusions</p>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                </button>
              </CardContent>
            </Card>
          </div>

          {/* Follow-up Settings */}
          <div>
            <h3 className="text-sm font-semibold mb-3 text-muted-foreground">Follow-Up</h3>
            <Card>
              <CardContent className="p-0">
                <button className="w-full p-4 text-left hover:bg-muted/50 transition-colors flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Send className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium text-sm">Email Templates</p>
                      <p className="text-xs text-muted-foreground">Customize follow-up messages</p>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                </button>
                <Separator />
                <button className="w-full p-4 text-left hover:bg-muted/50 transition-colors flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Clock className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium text-sm">Schedule & Timing</p>
                      <p className="text-xs text-muted-foreground">Set follow-up intervals</p>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                </button>
                <Separator />
                <button className="w-full p-4 text-left hover:bg-muted/50 transition-colors flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Bell className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium text-sm">Notifications</p>
                      <p className="text-xs text-muted-foreground">Email and push alerts</p>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                </button>
              </CardContent>
            </Card>
          </div>

          {/* App Settings */}
          <div>
            <h3 className="text-sm font-semibold mb-3 text-muted-foreground">App</h3>
            <Card>
              <CardContent className="p-0">
                <button className="w-full p-4 text-left hover:bg-muted/50 transition-colors flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Download className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium text-sm">Offline Mode</p>
                      <p className="text-xs text-muted-foreground">Work without internet</p>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                </button>
              </CardContent>
            </Card>
          </div>

          {/* Contact & Support */}
          <div>
            <h3 className="text-sm font-semibold mb-3 text-muted-foreground">Support</h3>
            <Card>
              <CardContent className="p-0">
                <button 
                  onClick={() => setTallyOpen(true)}
                  className="w-full p-4 text-left hover:bg-muted/50 transition-colors flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium text-sm">Schedule a Demo</p>
                      <p className="text-xs text-muted-foreground">Get personalized help</p>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                </button>
              </CardContent>
            </Card>
          </div>
        </div>
      </ScrollArea>
    </div>
  );

  return (
    <div className="h-screen flex flex-col bg-background max-w-md mx-auto">
      {/* Mobile Header */}
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="JobWalk AI" className="h-10 w-auto" />
          </div>
          <Button variant="ghost" size="icon">
            <Bell className="h-5 w-5" />
          </Button>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 overflow-hidden p-4">
        {currentView === 'home' && <HomeView />}
        {currentView === 'new-jobwalk' && <NewJobWalkView />}
        {currentView === 'proposals' && <ProposalsView />}
        {currentView === 'settings' && <SettingsView />}
      </main>

      {/* Bottom Navigation */}
      <nav className="border-t bg-background sticky bottom-0">
        <div className="grid grid-cols-4 gap-1 p-2">
          <Button
            variant={currentView === 'home' ? 'default' : 'ghost'}
            className="flex flex-col h-auto py-2 gap-1"
            onClick={() => setCurrentView('home')}
          >
            <Home className="h-5 w-5" />
            <span className="text-xs">Home</span>
          </Button>
          <Button
            variant={currentView === 'new-jobwalk' ? 'default' : 'ghost'}
            className="flex flex-col h-auto py-2 gap-1"
            onClick={() => setCurrentView('new-jobwalk')}
          >
            <PlusCircle className="h-5 w-5" />
            <span className="text-xs">New Walk</span>
          </Button>
          <Button
            variant={currentView === 'proposals' ? 'default' : 'ghost'}
            className="flex flex-col h-auto py-2 gap-1"
            onClick={() => setCurrentView('proposals')}
          >
            <FileCheck className="h-5 w-5" />
            <span className="text-xs">Proposals</span>
          </Button>
          <Button
            variant={currentView === 'settings' ? 'default' : 'ghost'}
            className="flex flex-col h-auto py-2 gap-1"
            onClick={() => setCurrentView('settings')}
          >
            <Settings className="h-5 w-5" />
            <span className="text-xs">Settings</span>
          </Button>
        </div>
      </nav>

      {/* Tally Form Dialog */}
      <TallyDialog open={tallyOpen} onOpenChange={setTallyOpen} />
    </div>
  );
};

export default MobileApp;
